import React from 'react';
import { Progress, Tag, Empty } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment-jalaali';

const STEP_STATUS = {
  approved: {
    color: '#16a34a',
    bg: 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800',
    icon: <CheckCircleOutlined />,
  },
  auto_approved: {
    color: '#16a34a',
    bg: 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800',
    icon: <CheckCircleOutlined />,
  },
  pending: {
    color: '#d97706',
    bg: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800',
    icon: <ClockCircleOutlined />,
  },
  rejected: {
    color: '#dc2626',
    bg: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800',
    icon: <CloseCircleOutlined />,
  },
  skipped: {
    color: '#94a3b8',
    bg: 'bg-slate-50 dark:bg-slate-800/40 border-slate-200 dark:border-slate-700',
    icon: <MinusCircleOutlined />,
  },
};

const formatActionAt = (value) => {
  if (!value) return null;
  const m = moment(value);
  return m.isValid() ? m.format('jYYYY/jMM/jDD HH:mm') : String(value);
};

/**
 * تایم‌لاین عمودی مراحل تأیید فرآیند (عمومی ERP)
 */
const WorkflowTimeline = ({
  workflow = null,
  steps: stepsProp = null,
  title = null,
  showProgress = true,
  compact = false,
}) => {
  const steps = Array.isArray(stepsProp)
    ? stepsProp
    : Array.isArray(workflow?.steps)
      ? workflow.steps
      : [];

  if (!workflow && steps.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={<span style={{ fontFamily: 'CustomFont' }}>فرآیند تأییدی ثبت نشده</span>}
      />
    );
  }

  const currentOrder = Number(workflow?.current_step_order) || 0;
  const progress = Number(workflow?.progress_percentage);
  const statusLabel = workflow?.status_label || workflow?.status;
  const workflowName = title || workflow?.workflow_name || workflow?.workflow_definition?.name;

  return (
    <div className="custom-font space-y-3" style={{ fontFamily: 'CustomFont' }}>
      {(workflowName || statusLabel || showProgress) && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            {workflowName ? (
              <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {workflowName}
              </div>
            ) : null}
            {statusLabel ? (
              <Tag
                className="custom-font m-0 mt-1"
                color={
                  workflow?.status === 'approved' || workflow?.status === 'auto_approved'
                    ? 'success'
                    : workflow?.status === 'rejected'
                      ? 'error'
                      : workflow?.status === 'canceled'
                        ? 'default'
                        : 'processing'
                }
              >
                {statusLabel}
              </Tag>
            ) : null}
          </div>
          {showProgress && Number.isFinite(progress) ? (
            <div className="min-w-[140px] flex-1 max-w-[220px]">
              <Progress
                percent={Math.round(progress)}
                size="small"
                status={
                  workflow?.status === 'rejected'
                    ? 'exception'
                    : workflow?.status === 'approved' || workflow?.status === 'auto_approved'
                      ? 'success'
                      : 'active'
                }
              />
              {workflow?.total_steps ? (
                <div className="text-[11px] text-slate-500 mt-0.5">
                  مرحله {currentOrder || '—'} از {workflow.total_steps}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      )}

      <div className={`relative ${compact ? 'space-y-2' : 'space-y-3'}`}>
        {steps.map((step, index) => {
          const meta = STEP_STATUS[step.status] || STEP_STATUS.pending;
          const isCurrent =
            step.status === 'pending' &&
            (Number(step.step_order) === currentOrder ||
              (!currentOrder && index === steps.findIndex((s) => s.status === 'pending')));
          const approverName =
            step.actual_approver?.name ||
            step.assigned_role_name ||
            (step.approver_type === 'direct_manager' ? 'مدیر مستقیم' : null);

          return (
            <div
              key={step.id || `${step.step_order}-${index}`}
              className={`relative rounded-xl border px-3 py-2.5 ${meta.bg} ${
                isCurrent ? 'ring-2 ring-amber-300/70 dark:ring-amber-600/50' : ''
              }`}
            >
              <div className="flex items-start gap-2.5">
                <span style={{ color: meta.color, fontSize: 18 }} className="mt-0.5">
                  {meta.icon}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-slate-500 tabular-nums">
                      مرحله {step.step_order ?? index + 1}
                    </span>
                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                      {step.name || 'مرحله تأیید'}
                    </span>
                    <Tag className="custom-font m-0 text-[10px]" color={
                      step.status === 'approved' || step.status === 'auto_approved'
                        ? 'success'
                        : step.status === 'rejected'
                          ? 'error'
                          : step.status === 'pending'
                            ? 'warning'
                            : 'default'
                    }>
                      {step.status_label || step.status}
                    </Tag>
                    {isCurrent ? (
                      <Tag color="gold" className="custom-font m-0 text-[10px]">
                        در حال بررسی
                      </Tag>
                    ) : null}
                  </div>
                  {approverName ? (
                    <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                      {step.actual_approver ? `تأییدکننده: ${approverName}` : `مسئول: ${approverName}`}
                    </div>
                  ) : null}
                  {step.comment ? (
                    <div
                      className={`text-xs mt-1.5 rounded-lg px-2 py-1 ${
                        step.status === 'rejected'
                          ? 'bg-red-100/80 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                          : 'bg-white/70 dark:bg-slate-900/40 text-slate-600 dark:text-slate-300'
                      }`}
                    >
                      {step.comment}
                    </div>
                  ) : null}
                  {step.action_at ? (
                    <div className="text-[11px] text-slate-400 mt-1 tabular-nums" dir="ltr">
                      {formatActionAt(step.action_at)}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WorkflowTimeline;
